import type { SupportedLanguage } from '../types';

export type TranslationKey = 
  | 'settings'
  | 'languageLevel'
  | 'age'
  | 'nativeLanguage'
  | 'conversationType'
  | 'speechSpeed'
  | 'saveAndClose'
  | 'autoPlayAudio'
  | 'beginner'
  | 'intermediate'
  | 'advanced'
  | 'fluent'
  | 'casual'
  | 'formal'
  | 'business'
  | 'academic'
  | 'selectTutor'
  | 'newConversation'
  | 'typeMessage'
  | 'shortAudio'
  | 'waitingForAI'
  | 'originalAnswer'
  | 'simplification1x'
  | 'simplification2x'
  | 'simplification3x'
  | 'normalAnswer'
  | 'simplified'
  | 'simplest'
  | 'tutor'
  | 'selectYourTutor'
  | 'confirmationModal.title'
  | 'confirmationModal.message'
  | 'confirmationModal.cancel'
  | 'confirmationModal.confirm'
  | 'startNewConversationButton';

export const translations: Record<SupportedLanguage, Record<TranslationKey, string>> = {
  en: {
    settings: 'Settings',
    languageLevel: 'Language Level',
    age: 'Age',
    nativeLanguage: 'Native Language',
    conversationType: 'Conversation Type',
    speechSpeed: 'Speech Speed',
    saveAndClose: 'Save & Close',
    autoPlayAudio: 'Auto-play Audio',
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
    fluent: 'Fluent',
    casual: 'Casual',
    formal: 'Formal',
    business: 'Business',
    academic: 'Academic',
    selectTutor: 'Please select a tutor to start chatting',
    newConversation: 'New Conversation',
    typeMessage: 'Type your message...',
    shortAudio: 'Audio too short, please record a longer message',
    waitingForAI: 'Waiting for AI response...',
    originalAnswer: 'Original Answer',
    simplification1x: 'Simplified 1x',
    simplification2x: 'Simplified 2x',
    simplification3x: 'Simplified 3x',
    normalAnswer: 'Normal Answer',
    simplified: 'Simplified',
    simplest: 'Simplest',
    tutor: 'Tutor',
    selectYourTutor: 'Select Your Tutor',
    'confirmationModal.title': 'Start New Conversation',
    'confirmationModal.message': 'Are you sure you want to start a new conversation?',
    'confirmationModal.cancel': 'Cancel',
    'confirmationModal.confirm': 'Confirm',
    startNewConversationButton: 'Start New Conversation'
  },
  pt: {
    settings: 'Configurações',
    languageLevel: 'Nível de Idioma',
    age: 'Idade',
    nativeLanguage: 'Língua Nativa',
    conversationType: 'Tipo de Conversa',
    speechSpeed: 'Velocidade da Fala',
    saveAndClose: 'Salvar e Fechar',
    autoPlayAudio: 'Reproduzir Áudio Automaticamente',
    beginner: 'Iniciante',
    intermediate: 'Intermediário',
    advanced: 'Avançado',
    fluent: 'Fluente',
    casual: 'Casual',
    formal: 'Formal',
    business: 'Negócios',
    academic: 'Académico',
    selectTutor: 'Por favor selecione um tutor para começar a conversar',
    newConversation: 'Nova Conversa',
    typeMessage: 'Digite sua mensagem...',
    shortAudio: 'Áudio muito curto, por favor grave uma mensagem mais longa',
    waitingForAI: 'Aguardando resposta da IA...',
    originalAnswer: 'Resposta Original',
    simplification1x: 'Simplificado 1x',
    simplification2x: 'Simplificado 2x',
    simplification3x: 'Simplificado 3x',
    normalAnswer: 'Resposta Normal',
    simplified: 'Simplificado',
    simplest: 'Mais Simples',
    tutor: 'Tutor',
    selectYourTutor: 'Selecione Seu Tutor',
    'confirmationModal.title': 'Iniciar Nova Conversa',
    'confirmationModal.message': 'Tem certeza que deseja iniciar uma nova conversa?',
    'confirmationModal.cancel': 'Cancelar',
    'confirmationModal.confirm': 'Confirmar',
    startNewConversationButton: 'Iniciar Nova Conversa'
  },
  es: {
    settings: 'Ajustes',
    languageLevel: 'Nivel de Idioma',
    age: 'Edad',
    nativeLanguage: 'Idioma Nativo',
    conversationType: 'Tipo de Conversación',
    speechSpeed: 'Velocidad del Habla',
    saveAndClose: 'Guardar y Cerrar',
    autoPlayAudio: 'Reproducir Audio Automáticamente',
    beginner: 'Principiante',
    intermediate: 'Intermedio',
    advanced: 'Avanzado',
    fluent: 'Fluido',
    casual: 'Casual',
    formal: 'Formal',
    business: 'Negocios',
    academic: 'Académico',
    selectTutor: 'Por favor seleccione un tutor para empezar a chatear',
    newConversation: 'Nueva Conversación',
    typeMessage: 'Escribe tu mensaje...',
    shortAudio: 'Audio demasiado corto, por favor graba un mensaje más largo',
    waitingForAI: 'Esperando respuesta de la IA...',
    originalAnswer: 'Respuesta Original',
    simplification1x: 'Simplificado 1x',
    simplification2x: 'Simplificado 2x',
    simplification3x: 'Simplificado 3x',
    normalAnswer: 'Respuesta Normal',
    simplified: 'Simplificado',
    simplest: 'Más Simple',
    tutor: 'Tutor',
    selectYourTutor: 'Seleccione Su Tutor',
    'confirmationModal.title': 'Iniciar Nueva Conversación',
    'confirmationModal.message': '¿Estás seguro de que quieres iniciar una nueva conversación?',
    'confirmationModal.cancel': 'Cancelar',
    'confirmationModal.confirm': 'Confirmar',
    startNewConversationButton: 'Iniciar Nueva Conversación'
  },
  fr: {
    settings: 'Paramètres',
    languageLevel: 'Niveau de Langue',
    age: 'Âge',
    nativeLanguage: 'Langue Maternelle',
    conversationType: 'Type de Conversation',
    speechSpeed: 'Vitesse de Parole',
    saveAndClose: 'Enregistrer et Fermer',
    autoPlayAudio: 'Lecture Audio Automatique',
    beginner: 'Débutant',
    intermediate: 'Intermédiaire',
    advanced: 'Avancé',
    fluent: 'Courant',
    casual: 'Décontracté',
    formal: 'Formel',
    business: 'Affaires',
    academic: 'Académique',
    selectTutor: 'Veuillez sélectionner un tuteur pour commencer à discuter',
    newConversation: 'Nouvelle Conversation',
    typeMessage: 'Tapez votre message...',
    shortAudio: 'Audio trop court, veuillez enregistrer un message plus long',
    waitingForAI: 'En attente de la réponse de l\'IA...',
    originalAnswer: 'Réponse Originale',
    simplification1x: 'Simplifié 1x',
    simplification2x: 'Simplifié 2x',
    simplification3x: 'Simplifié 3x',
    normalAnswer: 'Réponse Normale',
    simplified: 'Simplifié',
    simplest: 'Le Plus Simple',
    tutor: 'Tuteur',
    selectYourTutor: 'Sélectionnez Votre Tuteur',
    'confirmationModal.title': 'Démarrer une Nouvelle Conversation',
    'confirmationModal.message': 'Êtes-vous sûr de vouloir démarrer une nouvelle conversation?',
    'confirmationModal.cancel': 'Annuler',
    'confirmationModal.confirm': 'Confirmer',
    startNewConversationButton: 'Démarrer une Nouvelle Conversation'
  },
  de: {
    settings: 'Einstellungen',
    languageLevel: 'Sprachniveau',
    age: 'Alter',
    nativeLanguage: 'Muttersprache',
    conversationType: 'Gesprächstyp',
    speechSpeed: 'Sprechgeschwindigkeit',
    saveAndClose: 'Speichern et Schließen',
    autoPlayAudio: 'Audio automatisch abspielen',
    beginner: 'Anfänger',
    intermediate: 'Mittelstufe',
    advanced: 'Fortgeschritten',
    fluent: 'Fließend',
    casual: 'Casual',
    formal: 'Formell',
    business: 'Geschäftlich',
    academic: 'Akademisch',
    selectTutor: 'Bitte wählen Sie einen Tutor aus, um mit dem Chat zu beginnen',
    newConversation: 'Neue Konversation',
    typeMessage: 'Nachricht eingeben...',
    shortAudio: 'Audio zu kurz, bitte nehmen Sie eine längere Nachricht auf',
    waitingForAI: 'Warte auf KI-Antwort...',
    originalAnswer: 'Originale Antwort',
    simplification1x: 'Vereinfacht 1x',
    simplification2x: 'Vereinfacht 2x',
    simplification3x: 'Vereinfacht 3x',
    normalAnswer: 'Normale Antwort',
    simplified: 'Vereinfacht',
    simplest: 'Am Einfachsten',
    tutor: 'Tutor',
    selectYourTutor: 'Wählen Sie Ihren Tutor',
    'confirmationModal.title': 'Neue Konversation starten',
    'confirmationModal.message': 'Sind Sie sicher, dass Sie eine neue Konversation starten möchten?',
    'confirmationModal.cancel': 'Abbrechen',
    'confirmationModal.confirm': 'Bestätigen',
    startNewConversationButton: 'Neue Konversation starten'
  },
  ru: {
    settings: 'Настройки',
    languageLevel: 'Уровень Языка',
    age: 'Возраст',
    nativeLanguage: 'Родной Язык',
    conversationType: 'Тип Разговора',
    speechSpeed: 'Скорость Речи',
    saveAndClose: 'Сохранить и Закрыть',
    autoPlayAudio: 'Автовоспроизведение Аудио',
    beginner: 'Начинающий',
    intermediate: 'Средний',
    advanced: 'Продвинутый',
    fluent: 'Свободный',
    casual: 'Повседневный',
    formal: 'Формальный',
    business: 'Деловой',
    academic: 'Академический',
    selectTutor: 'Пожалуйста, выберите преподавателя, чтобы начать чат',
    newConversation: 'Новый Разговор',
    typeMessage: 'Введите ваше сообщение...',
    shortAudio: 'Слишком короткое аудио, пожалуйста, запишите более длинное сообщение',
    waitingForAI: 'Ожидание ответа ИИ...',
    originalAnswer: 'Оригинальный Ответ',
    simplification1x: 'Упрощенный 1x',
    simplification2x: 'Упрощенный 2x',
    simplification3x: 'Упрощенный 3x',
    normalAnswer: 'Обычный Ответ',
    simplified: 'Упрощенный',
    simplest: 'Самый Простой',
    tutor: 'Преподаватель',
    selectYourTutor: 'Выберите Вашего Преподавателя',
    'confirmationModal.title': 'Начать Новый Разговор',
    'confirmationModal.message': 'Вы уверены, что хотите начать новый разговор?',
    'confirmationModal.cancel': 'Отмена',
    'confirmationModal.confirm': 'Подтвердить',
    startNewConversationButton: 'Начать Новый Разговор'
  },
  it: {
    settings: 'Impostazioni',
    languageLevel: 'Livello di Lingua',
    age: 'Età',
    nativeLanguage: 'Lingua Madre',
    conversationType: 'Tipo di Conversazione',
    speechSpeed: 'Velocità del Parlato',
    saveAndClose: 'Salva e Chiudi',
    autoPlayAudio: 'Riproduzione Audio Automatica',
    beginner: 'Principiante',
    intermediate: 'Intermedio',
    advanced: 'Avanzato',
    fluent: 'Fluente',
    casual: 'Informale',
    formal: 'Formale',
    business: 'Affari',
    academic: 'Accademico',
    selectTutor: 'Seleziona un tutor per iniziare a chattare',
    newConversation: 'Nuova Conversazione',
    typeMessage: 'Scrivi il tuo messaggio...',
    shortAudio: 'Audio troppo breve, registra un messaggio più lungo',
    waitingForAI: 'In attesa della risposta AI...',
    originalAnswer: 'Risposta Originale',
    simplification1x: 'Semplificato 1x',
    simplification2x: 'Semplificato 2x',
    simplification3x: 'Semplificato 3x',
    normalAnswer: 'Risposta Normale',
    simplified: 'Semplificato',
    simplest: 'Più Semplice',
    tutor: 'Tutor',
    selectYourTutor: 'Seleziona il Tuo Tutor',
    'confirmationModal.title': 'Inizia Nuova Conversazione',
    'confirmationModal.message': 'Sei sicuro di voler iniziare una nuova conversazione?',
    'confirmationModal.cancel': 'Annulla',
    'confirmationModal.confirm': 'Conferma',
    startNewConversationButton: 'Inizia Nuova Conversazione'
  }
};

export function getTranslation(key: TranslationKey, language: SupportedLanguage = 'en'): string {
  return translations[language][key] || translations['en'][key];
}
